// @flow

import { PaymentType, threeDSecure as threeDSecureClient } from '@ebanx/ebanx-js';
import { getCountryNationality } from '@tectonics/prs/country/types';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type IncomingPayment, getCountry, getReceipts, getCustomerEmail, getSafeCustomerName } from '@tectonics/prs/incoming-payment';
import { getAmountLocalBase } from '@tectonics/prs/receipts';
import { getAmountValue, getAmountCurrencyCode } from '@tectonics/prs/money/amount';
import { type ThreeDSecureResponse } from './types';
import { getExpiryMonthFromCardExpiration, getExpiryYearFromCardExpiration } from './format-utils';
import ThreedsecureMode from '@tectonics/prs/threedsecure/threedsecure-mode';

export async function threeDSecure(userInputValues: UserInputValues, incomingPayment: IncomingPayment, threeDSecureMode: ThreedsecureMode): Promise<?ThreeDSecureResponse> {
  const country = getCountry(incomingPayment);
  const nationality = getCountryNationality(country);

  const threeDSecureOptions = {
    orderInformation: {
      amountDetails: {
        totalAmount: getAmountValue(getAmountLocalBase(getReceipts(incomingPayment))),
        currency: getAmountCurrencyCode(getAmountLocalBase(getReceipts(incomingPayment))),
      },
      billTo: {
        address1: userInputValues[nationality+'Address'],
        administrativeArea: userInputValues[nationality+'State'],
        country: toUpper(country),
        email: userInputValues['customerEmail'] || getCustomerEmail(incomingPayment),
        homePhone: userInputValues[nationality+'PhoneNumber'],
        locality: userInputValues[nationality+'City'],
        postalCode: userInputValues[nationality+'Zipcode'],
        mobilePhone: userInputValues[nationality+'PhoneNumber'],
      },
    },
    paymentInformation: {
      card: {
        number: userInputValues['cardNumber'],
        expirationMonth: getExpiryMonthFromCardExpiration(userInputValues['cardExpiryDate']),
        expirationYear: getExpiryYearFromCardExpiration(userInputValues['cardExpiryDate']),
        holderName: userInputValues['cardHolderName'] || userInputValues['customerName'] || getSafeCustomerName(incomingPayment),
      },
      paymentMethod: userInputValues['selectedPaymentMethod'] || PaymentType.DEBITCARD,
      dataOnly: threeDSecureMode === ThreedsecureMode.DATA_ONLY,
    },
    personalIdentification: {
      id: userInputValues[nationality+'Document'],
      type: toUpper(userInputValues[nationality+'DocumentType']),
    },
  };

  return threeDSecureClient.authenticate(threeDSecureOptions);
}

function toUpper(text: string): string {
  if (typeof text === 'string') {
    return text.toUpperCase();
  }
  return '';
}
